import React, { useState, useEffect, FunctionComponent } from 'react';
import { Environment } from 'react-relay';
import { useSelector } from 'react-redux';

import {
    shouldSeeConsumerSourcingTooltip,
    shouldSuppressMessage,
    trackConsumerSourcingTooltip,
} from './helpers/pdpSharedConsumerSourcingTooltipDisplayHelpers';

const PdpRespConsumerSourcingTooltip = React.lazy(() =>
    import(
        /* webpackChunkName: "PdpRespConsumerSourcingTooltip" */ './PdpRespConsumerSourcingTooltip'
    )
);

type ReduxState = {
    relayVariables: {
        variables: {
            itemId: string;
        };
    };
};

type ComponentProps = {
    userId: string;
    environment: Environment;
    forceConsumerSourcingTooltip?: boolean;
};

export const PdpRespConsumerSourcingTooltipLoader: FunctionComponent<ComponentProps> = ({
    userId,
    environment,
    forceConsumerSourcingTooltip = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [itemInfo, setItemInfo] = useState({ itemTitle: '', itemReferenceNumber: '' });
    const relayVariables = useSelector((state: ReduxState) => state.relayVariables.variables);
    const itemId = relayVariables?.itemId;
    useEffect(() => {
        const modifyModalOpenState = async (): Promise<void> => {
            if (itemId && !shouldSuppressMessage({ userId })) {
                // continue to register PDP view regardless of `forceConsumerSourcingTooltip`
                const res = await shouldSeeConsumerSourcingTooltip({
                    itemId,
                    userId,
                    environment,
                });
                const showTooltip = forceConsumerSourcingTooltip || res.showTooltip;
                setIsOpen(showTooltip);

                if (showTooltip) {
                    setItemInfo({
                        itemTitle: res?.itemTitle || '',
                        itemReferenceNumber: res?.itemReferenceNumber || '',
                    });
                    trackConsumerSourcingTooltip({ action: 'modal view' });
                }
            }
        };
        modifyModalOpenState();
    }, [environment, itemId, userId, forceConsumerSourcingTooltip]);

    return isOpen ? (
        <React.Suspense fallback={null}>
            <PdpRespConsumerSourcingTooltip
                itemId={itemId}
                onClose={() => setIsOpen(false)}
                userId={userId}
                environment={environment}
                {...itemInfo}
            />
        </React.Suspense>
    ) : null;
};

export default PdpRespConsumerSourcingTooltipLoader;
